interface RateLimitData {
  attempts: number;
  firstAttemptTime: number;
  blockedUntil?: number;
}

interface RateLimitConfig {
  maxAttempts: number;
  windowMs: number;
  blockDurationMs: number;
}

const DEFAULT_CONFIG: RateLimitConfig = {
  maxAttempts: 5, // Maximum attempts allowed
  windowMs: 15 * 60 * 1000, // 15 minute window
  blockDurationMs: 30 * 60 * 1000 // 30 minute block after exceeding
};

class RateLimiter {
  private storagePrefix = "rateLimit_";

  /**
   * Check if an action is rate limited
   * @param key - Unique identifier for the rate limit (e.g., 'verify_email@example.com')
   * @param config - Optional custom configuration
   * @returns Object with isBlocked status and details
   */
  checkRateLimit(
    key: string,
    config: Partial<RateLimitConfig> = {}
  ): {
    isBlocked: boolean;
    remainingAttempts: number;
    blockedUntil?: Date;
    message?: string;
  } {
    const fullConfig = {...DEFAULT_CONFIG, ...config};
    const storageKey = this.storagePrefix + key;
    const now = Date.now();

    // Get existing data
    const storedData = this.getData(storageKey);

    // Check if currently blocked
    if (storedData?.blockedUntil && storedData.blockedUntil > now) {
      const blockedUntilDate = new Date(storedData.blockedUntil);
      const minutesRemaining = Math.ceil((storedData.blockedUntil - now) / 60000);
      return {
        isBlocked: true,
        remainingAttempts: 0,
        blockedUntil: blockedUntilDate,
        message: `Too many attempts. Please try again in ${minutesRemaining} minute${minutesRemaining > 1 ? "s" : ""}.`
      };
    }

    // Check if window has expired
    if (storedData && now - storedData.firstAttemptTime > fullConfig.windowMs) {
      // Reset the window
      this.clearData(storageKey);
      return {
        isBlocked: false,
        remainingAttempts: fullConfig.maxAttempts
      };
    }

    // Calculate remaining attempts
    const attempts = storedData?.attempts || 0;
    const remainingAttempts = Math.max(0, fullConfig.maxAttempts - attempts);

    return {
      isBlocked: false,
      remainingAttempts,
      message:
        remainingAttempts > 0
          ? `${remainingAttempts} attempt${remainingAttempts !== 1 ? "s" : ""} remaining`
          : undefined
    };
  }

  /**
   * Record an attempt for rate limiting
   * @param key - Unique identifier for the rate limit
   * @param config - Optional custom configuration
   * @returns Object with updated rate limit status
   */
  recordAttempt(
    key: string,
    config: Partial<RateLimitConfig> = {}
  ): {
    isNowBlocked: boolean;
    remainingAttempts: number;
    blockedUntil?: Date;
    message?: string;
  } {
    const fullConfig = {...DEFAULT_CONFIG, ...config};
    const storageKey = this.storagePrefix + key;
    const now = Date.now();

    let data = this.getData(storageKey);

    // Initialize or reset if window expired
    if (!data || now - data.firstAttemptTime > fullConfig.windowMs) {
      data = {
        attempts: 0,
        firstAttemptTime: now
      };
    }

    // Increment attempts
    data.attempts++;

    // Check if should block
    if (data.attempts >= fullConfig.maxAttempts) {
      data.blockedUntil = now + fullConfig.blockDurationMs;
      this.setData(storageKey, data);

      const blockedUntilDate = new Date(data.blockedUntil);
      const minutesBlocked = Math.ceil(fullConfig.blockDurationMs / 60000);

      return {
        isNowBlocked: true,
        remainingAttempts: 0,
        blockedUntil: blockedUntilDate,
        message: `Too many attempts. You have been blocked for ${minutesBlocked} minutes.`
      };
    }

    // Save updated data
    this.setData(storageKey, data);

    const remainingAttempts = fullConfig.maxAttempts - data.attempts;

    return {
      isNowBlocked: false,
      remainingAttempts,
      message: `${remainingAttempts} attempt${remainingAttempts !== 1 ? "s" : ""} remaining`
    };
  }

  /**
   * Clear rate limit data for a specific key
   * @param key - Unique identifier for the rate limit
   */
  clearRateLimit(key: string): void {
    const storageKey = this.storagePrefix + key;
    this.clearData(storageKey);
  }

  /**
   * Clear all rate limit data
   */
  clearAllRateLimits(): void {
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith(this.storagePrefix)) {
        localStorage.removeItem(key);
      }
    });
  }

  private getData(key: string): RateLimitData | null {
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  }

  private setData(key: string, data: RateLimitData): void {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error("Failed to save rate limit data:", error);
    }
  }

  private clearData(key: string): void {
    localStorage.removeItem(key);
  }
}

// Export singleton instance
export const rateLimiter = new RateLimiter();

// Export types for use in components
export type {RateLimitConfig, RateLimitData};
